import { Fade, useTheme } from '@mui/material';
import { keyBy } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { AddButton, EditableBloc, IconTitle, VerticalLine } from '../../components';
import { Condominium } from '../../entities';
import { Locker, LockerVendor } from '../../entities/locker.entity';
import FlexContainer from '../../components/FlexContainer';
import LockerPanel from './components/LockerPanel';
import NoContent from './components/NoContent';
import useStyles from './styles';
import lockerSvg from '../../assets/locker.svg';
import { useCondominiums, useFetch, useLockers } from '../../hooks';

export const Lockers = (): JSX.Element => {
  const { spacing } = useTheme();
  const repository = useLockers();
  const condominiumsRepository = useCondominiums();
  const { enqueueSnackbar } = useSnackbar();
  const [lockers, setLockers] = useState<Locker[]>([]);
  const [condominiums, setCondominiums] = useState<Condominium[]>([]);
  const [condominiumsMapper, setCondominiumsMapper] = useState<Record<string, Condominium>>(
    {} as Record<string, Condominium>
  );
  const styles = useStyles();
  const [selectedLocker, setSelectedLocker] = useState<Locker>({} as Locker);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const { data: condominiumsResponse } = useFetch(condominiumsRepository.fetchAll);
  const { data: lockersResponse, loading, refetch } = useFetch(repository.fetchAll);

  const [inProgress, setInProgress] = useState<boolean>(false);

  useEffect(() => {
    setCondominiums(condominiumsResponse ?? []);

    const mapper = keyBy(condominiumsResponse, 'id');
    setCondominiumsMapper(mapper);
  }, [condominiumsResponse]);

  useEffect(() => {
    setLockers(lockersResponse ?? []);
  }, [lockersResponse]);

  const handleSetPanelOpen = () => {
    setSelectedLocker({} as Locker);
    setIsPanelVisible(!isPanelVisible);
  };

  const formatVendor = (vendor: string) => {
    const mapping: Record<string, string> = {
        'entrega_ali': 'Entrega Ali',
        'clique_retire': 'Clique Retire'
    };
    return mapping[vendor] || vendor;
};


  const handleSubmit = async (locker: Locker) => {
    try {
      if (locker.id) {
        await repository.update(locker);
      } else {
        await repository.create(locker);
      }

      setIsPanelVisible(false);
      refetch();
      enqueueSnackbar('Armário salvo com sucesso!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Não foi possível criar um armário agora', { variant: 'error' });
    }
  };

  const handleSelectLocker = (locker?: Locker) => {
    if (locker) {
      setSelectedLocker(locker);
    }

    setIsPanelVisible(true);
  };

  const handleSync = async (locker?: Locker) => {
    try {
      if (locker?.vendor === LockerVendor.CliqueRetire) {
        setInProgress(true);
        await repository.sync(locker);
        setInProgress(false);
        refetch();

        enqueueSnackbar('Sincronização realizada com sucesso!', { variant: 'success' });
      }
    } catch (err) {
      setInProgress(false);
      enqueueSnackbar('Não foi possível sincronizar os dados agora', { variant: 'error' });
    }
    setIsPanelVisible(true);
  };

  const showNoContent = useMemo(() => !isPanelVisible && lockers.length === 0, [isPanelVisible, lockers]);

  if (loading && lockers.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de armários" />
          <FlexContainer>
            {lockers.map((locker) => (
              <EditableBloc<Locker>
                key={locker.id}
                label={`#Armário-${locker.id.substring(0, 4)}`}
                value={locker}
                width={`calc(33.3% - ${spacing(1.33)})`}
                maxWidth="600px"
                minWidth="250px"
                onClickEdit={handleSelectLocker}
                prepend={<div className={styles.image} style={{ backgroundImage: `url(${lockerSvg})` }} />}
              >
                <div className={styles.info}>
                  <div>
                    Condomínio:{' '}
                    {condominiumsMapper[locker.condominiumId!]
                      ? condominiumsMapper[locker.condominiumId!].name
                      : 'SEM CONDOMÍNIO'}
                  </div>

                  <div>ID do parceiro: {locker.lockerId}</div>
                  <div>Parceiro: {formatVendor(locker.vendor)}</div>
                </div>
              </EditableBloc>
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <LockerPanel
        inProgress={inProgress}
        onSync={handleSync}
        visible={isPanelVisible}
        locker={selectedLocker}
        condominiums={condominiums}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
      />
      {!isPanelVisible && <AddButton label="adicionar armário" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default Lockers;
